export const ASSISTANT_MAX_MESSAGE_LENGTH = 4000;

export type AssistantErrorCode =
  | "not_configured"
  | "rate_limited"
  | "conversation_too_long"
  | "refused"
  | "upstream"
  | "internal"
  | "network"
  | "interrupted"
  | "unauthorized"
  | "forbidden"
  | "busy"
  | "invalid_request";

export interface AssistantError {
  code: AssistantErrorCode;
  message: string;
}

export type ToolActivityStatus = "running" | "ok" | "error";

export interface ToolActivity {
  id: string;
  name: string;
  title?: string;
  status: ToolActivityStatus;
  summary?: string;
}

export type ChatMessageStatus = "streaming" | "done" | "error" | "stopped";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  tools: ToolActivity[];
  status: ChatMessageStatus;
  error?: AssistantError;
  pendingBreak?: boolean;
  stopReason?: string;
  notice?: string;
}

export interface AssistantUsage {
  input_tokens?: number;
  output_tokens?: number;
  cache_read_input_tokens?: number;
}

export type AssistantStreamEvent =
  | { type: "conversation"; data: { conversationId: string; reset?: boolean; reason?: string } }
  | { type: "text"; data: { delta: string } }
  | { type: "tool_start"; data: { id: string; name: string; title?: string; input?: unknown } }
  | { type: "tool_end"; data: { id: string; name: string; ok: boolean; summary?: string } }
  | {
      type: "done";
      data: { conversationId: string; stopReason?: string; usage?: AssistantUsage };
    }
  | { type: "error"; data: { message?: string; code?: string } };

const STREAM_EVENT_TYPES = new Set([
  "conversation",
  "text",
  "tool_start",
  "tool_end",
  "done",
  "error",
]);

export function parseStreamEvent(event: string, data: string): AssistantStreamEvent | null {
  if (!STREAM_EVENT_TYPES.has(event)) return null;
  let parsed: any;
  try {
    parsed = data ? JSON.parse(data) : {};
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  if (event === "text" && typeof parsed.delta !== "string") return null;
  if ((event === "tool_start" || event === "tool_end") && typeof parsed.id !== "string") {
    return null;
  }
  return { type: event, data: parsed } as AssistantStreamEvent;
}

const KNOWN_ERROR_CODES = new Set<AssistantErrorCode>([
  "not_configured",
  "rate_limited",
  "conversation_too_long",
  "refused",
  "upstream",
  "internal",
  "network",
  "interrupted",
  "unauthorized",
  "forbidden",
  "busy",
  "invalid_request",
]);

export function normalizeErrorCode(code: unknown): AssistantErrorCode {
  if (typeof code === "string" && KNOWN_ERROR_CODES.has(code as AssistantErrorCode)) {
    return code as AssistantErrorCode;
  }
  if (
    code === "conversation_busy" ||
    code === "conflict" ||
    code === "locked" ||
    code === "in_progress"
  ) {
    return "busy";
  }
  return "internal";
}

export function errorCodeFromStatus(status: number, code?: unknown): AssistantErrorCode {
  if (typeof code === "string") {
    const normalized = normalizeErrorCode(code);
    if (normalized !== "internal" || code === "internal") return normalized;
  }
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 409) return "busy";
  if (status === 429) return "rate_limited";
  if (status === 503) return "not_configured";
  if (status === 400 || status === 413 || status === 422) return "invalid_request";
  if (status >= 500) return "upstream";
  return "internal";
}

export interface AssistantErrorCopy {
  title: string;
  description: string;
  canRetry: boolean;
  suggestNewChat: boolean;
}

export function describeAssistantError(error: AssistantError | undefined): AssistantErrorCopy {
  const code = error?.code ?? "internal";
  const serverMessage = error?.message?.trim();
  switch (code) {
    case "not_configured":
      return {
        title: "Assistant not configured",
        description:
          "The assistant isn't set up on the server yet. Ask an admin to configure the Anthropic API key.",
        canRetry: false,
        suggestNewChat: false,
      };
    case "rate_limited":
      return {
        title: "Slow down a little",
        description:
          "You've reached the assistant's request limit. Wait a few minutes, then try again.",
        canRetry: true,
        suggestNewChat: false,
      };
    case "conversation_too_long":
      return {
        title: "Conversation too long",
        description:
          "This chat has reached its length limit. Start a new chat to keep going.",
        canRetry: false,
        suggestNewChat: true,
      };
    case "refused":
      return {
        title: "Claude declined",
        description:
          "Claude declined to respond to this request. Try rephrasing it as a question about orders or customers.",
        canRetry: false,
        suggestNewChat: false,
      };
    case "busy":
      return {
        title: "Still working on another reply",
        description:
          "A reply is already being generated for this chat, possibly in another tab. Try again in a moment.",
        canRetry: true,
        suggestNewChat: false,
      };
    case "network":
      return {
        title: "Connection problem",
        description: "Couldn't reach the server. Check your connection and retry.",
        canRetry: true,
        suggestNewChat: false,
      };
    case "interrupted":
      return {
        title: "Response interrupted",
        description: "The connection closed before Claude finished. Retry to ask again.",
        canRetry: true,
        suggestNewChat: false,
      };
    case "unauthorized":
      return {
        title: "Session expired",
        description: "Your session expired. Sign in again to keep using the assistant.",
        canRetry: false,
        suggestNewChat: false,
      };
    case "forbidden":
      return {
        title: "No access",
        description: "Your role doesn't have permission to use the assistant.",
        canRetry: false,
        suggestNewChat: false,
      };
    case "invalid_request":
      return {
        title: "Couldn't send that message",
        description: serverMessage || "The server rejected this message.",
        canRetry: false,
        suggestNewChat: false,
      };
    case "upstream":
      return {
        title: "Claude is unavailable",
        description:
          serverMessage || "Claude is temporarily unavailable. Retry in a moment.",
        canRetry: true,
        suggestNewChat: false,
      };
    default:
      return {
        title: "Something went wrong",
        description: serverMessage || "The assistant hit an unexpected error.",
        canRetry: true,
        suggestNewChat: false,
      };
  }
}

const TOOL_LABELS: Record<string, { running: string; done: string }> = {
  search_orders: { running: "Searching orders", done: "Searched orders" },
  get_order: { running: "Loading order", done: "Loaded order" },
  get_order_timeline: { running: "Reading order timeline", done: "Read order timeline" },
  get_customer_orders: {
    running: "Looking up customer orders",
    done: "Looked up customer orders",
  },
  get_order_stats: { running: "Crunching order stats", done: "Crunched order stats" },
  find_stuck_orders: { running: "Finding stuck orders", done: "Checked for stuck orders" },
  track_shipment: { running: "Tracking shipment", done: "Tracked shipment" },
  get_order_payment_logs: { running: "Reading payment logs", done: "Read payment logs" },
  search_users: { running: "Searching customers", done: "Searched customers" },
  get_user: { running: "Loading customer", done: "Loaded customer" },
  get_user_activity: { running: "Reading customer activity", done: "Read customer activity" },
  get_user_cart_and_wishlist: {
    running: "Checking cart and wishlist",
    done: "Checked cart and wishlist",
  },
  get_user_stats: { running: "Crunching customer stats", done: "Crunched customer stats" },
  list_staff: { running: "Listing staff", done: "Listed staff" },
};

function humanizeToolName(name: string): string {
  const words = (name || "tool").replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function describeStopReason(stopReason: string | undefined): string | null {
  if (stopReason === "max_tokens") {
    return "The reply hit its length limit and may be cut off. Ask Claude to continue or narrow the question.";
  }
  if (stopReason === "max_iterations") {
    return "Claude stopped after too many lookups. Try a more specific question.";
  }
  return null;
}

export function toolActivityLabel(tool: Pick<ToolActivity, "name" | "title" | "status">): string {
  const known = TOOL_LABELS[tool.name];
  if (tool.status === "running") {
    return `${known?.running ?? tool.title ?? humanizeToolName(tool.name)}…`;
  }
  if (tool.status === "error") {
    return known?.running ?? tool.title ?? humanizeToolName(tool.name);
  }
  return known?.done ?? tool.title ?? humanizeToolName(tool.name);
}

export function createAssistantPlaceholder(id: string): ChatMessage {
  return { id, role: "assistant", text: "", tools: [], status: "streaming" };
}

export const CONVERSATION_RESET_NOTICE =
  "The assistant was updated since this chat started, so this reply doesn't remember the earlier messages.";
export const CONVERSATION_EXPIRED_NOTICE =
  "This chat expired on the server, so this reply doesn't remember the earlier messages.";

export function conversationResetNotice(reason: string | undefined): string {
  return reason === "not_found" ? CONVERSATION_EXPIRED_NOTICE : CONVERSATION_RESET_NOTICE;
}

export function applyStreamEvent(message: ChatMessage, event: AssistantStreamEvent): ChatMessage {
  switch (event.type) {
    case "conversation":
      return event.data.reset
        ? { ...message, notice: conversationResetNotice(event.data.reason) }
        : message;
    case "text": {
      if (!event.data.delta) return message;
      const needsBreak =
        message.pendingBreak &&
        message.text.trim().length > 0 &&
        !message.text.endsWith("\n") &&
        !event.data.delta.startsWith("\n");
      const separator = needsBreak ? "\n\n" : "";
      return {
        ...message,
        text: `${message.text}${separator}${event.data.delta}`,
        pendingBreak: false,
      };
    }
    case "tool_start": {
      const existing = message.tools.find((tool) => tool.id === event.data.id);
      const tools = existing
        ? message.tools.map((tool) =>
            tool.id === event.data.id ? { ...tool, status: "running" as const } : tool,
          )
        : [
            ...message.tools,
            {
              id: event.data.id,
              name: event.data.name,
              title: event.data.title,
              status: "running" as const,
            },
          ];
      return { ...message, tools, pendingBreak: true };
    }
    case "tool_end": {
      const status: ToolActivityStatus = event.data.ok ? "ok" : "error";
      const found = message.tools.some((tool) => tool.id === event.data.id);
      const tools = found
        ? message.tools.map((tool) =>
            tool.id === event.data.id
              ? { ...tool, status, summary: event.data.summary }
              : tool,
          )
        : [
            ...message.tools,
            { id: event.data.id, name: event.data.name, status, summary: event.data.summary },
          ];
      return { ...message, tools, pendingBreak: true };
    }
    case "done": {
      const done = finalizeMessage(message, "done");
      return event.data.stopReason ? { ...done, stopReason: event.data.stopReason } : done;
    }
    case "error":
      return finalizeMessage(message, "error", {
        code: normalizeErrorCode(event.data.code),
        message: event.data.message || "",
      });
    default:
      return message;
  }
}

export function finalizeMessage(
  message: ChatMessage,
  status: Exclude<ChatMessageStatus, "streaming">,
  error?: AssistantError,
): ChatMessage {
  const tools = message.tools.map((tool) =>
    tool.status === "running"
      ? { ...tool, status: status === "done" ? ("ok" as const) : ("error" as const) }
      : tool,
  );
  const next: ChatMessage = { ...message, tools, status, pendingBreak: false };
  if (error) next.error = error;
  else delete next.error;
  return next;
}

export interface UiHistoryMessage {
  role: "user" | "assistant";
  text: string;
  tools?: { id: string; name: string; title?: string; ok: boolean }[];
}

export function messagesFromHistory(
  history: UiHistoryMessage[] | undefined | null,
  idPrefix = "h",
): ChatMessage[] {
  if (!Array.isArray(history)) return [];
  return history
    .filter((item) => item && (item.role === "user" || item.role === "assistant"))
    .map((item, index) => ({
      id: `${idPrefix}-${index}`,
      role: item.role,
      text: typeof item.text === "string" ? item.text : "",
      tools: Array.isArray(item.tools)
        ? item.tools.map((tool) => ({
            id: tool.id,
            name: tool.name,
            title: tool.title,
            status: tool.ok ? ("ok" as const) : ("error" as const),
          }))
        : [],
      status: "done" as const,
    }));
}

export function findRetryTarget(
  messages: ChatMessage[],
): { userIndex: number; text: string } | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "user") return { userIndex: i, text: messages[i].text };
  }
  return null;
}
